/**
 * src/components/student-dashboard-components/theme/components/Modal.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing.md};
  ${createTransition('opacity', 'fast')};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.sm};
  `}
`;

const ModalContainer = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.xl};
  width: 100%;
  max-width: ${props => props.$maxWidth || '500px'};
  max-height: 90vh;
  overflow-y: auto;
  ${createTransition('transform', 'normal')};
  
  ${props => props.theme.isMobile && `
    max-width: 100%;
    max-height: 95vh;
    border-radius: ${props.theme.borderRadius.md};
  `}
`;

const ModalHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${getColor('neutral.medium')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
  `}
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h3.fontSize : 
    props.theme.typography.desktop.h3.fontSize};
  font-weight: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h3.fontWeight : 
    props.theme.typography.desktop.h3.fontWeight};
  color: ${getColor('primary.blue')};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${props => props.theme.spacing.sm};
  cursor: pointer;
  color: ${getColor('neutral.dark')};
  border-radius: ${props => props.theme.borderRadius.sm};
  ${createTransition('background', 'fast')};
  
  &:hover {
    background: ${getColor('neutral.light')};
  }
  
  &:focus {
    outline: 2px solid ${getColor('primary.skyBlue')};
    outline-offset: 2px;
  }
`;

const ModalContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
  `}
`;

const ModalFooter = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${getColor('neutral.medium')};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
    flex-direction: column-reverse;
    gap: ${props.theme.spacing.sm};
  `}
`;

/**
 * Modal.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  titleId,
  closeButtonLabel,
  children,
  maxWidth,
  showCloseButton = true,
  closeOnOverlayClick = true,
  className,
  footer,
  ...props
}) => {
  const theme = useTheme();
  
  if (!isOpen) return null;
  
    /**
   * Handle Overlay Click.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose?.();
    }
  };
  
  return (
    <ModalOverlay theme={theme} onClick={handleOverlayClick}>
      <ModalContainer theme={theme} $maxWidth={maxWidth} className={className} {...props}>
        {(title || showCloseButton) && (
          <ModalHeader theme={theme}>
            {title && <ModalTitle id={titleId} theme={theme}>{title}</ModalTitle>}
            {showCloseButton && (
              <CloseButton type="button" theme={theme} onClick={onClose} aria-label={closeButtonLabel}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            )}
          </ModalHeader>
        )}
        
        <ModalContent theme={theme}>
          {children}
        </ModalContent>
        
        {footer && (
          <ModalFooter theme={theme}>
            {footer}
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default Modal;
